import React, { useCallback, useEffect, useRef, useState } from 'react';
import isVideoFile from '../../utils/isVideoFile';
import { createMediaUploader } from '../../utils/mediaUpload';
import { normalizeMediaList } from '../../utils/postMediaUtils';
import LocalOpsSheet from './LocalOpsSheet';
import LocalOpsMediaPicker from './LocalOpsMediaPicker';

const TITLE_MAX_LENGTH = 240;
const DESCRIPTION_MAX_LENGTH = 2000;

const clampCoverIndex = (index, mediaLength) => {
  const parsed = Number(index);
  if (!Number.isInteger(parsed) || parsed < 0) return 0;
  if (!mediaLength) return 0;
  return Math.min(parsed, mediaLength - 1);
};

const revokeBlobPreview = (media) => {
  if (media?.preview?.startsWith('blob:')) {
    URL.revokeObjectURL(media.preview);
  }
};

const buildPersistedMediaItems = (item) =>
  normalizeMediaList(item).map((media) => ({
    url: media.url,
    preview: media.url,
    isVideo: media.type === 'video' || isVideoFile(media.url),
    persisted: true,
  }));

export default function LocalOpsPublicDetailsEditSheet({
  open,
  type,
  item,
  onClose,
  onSave,
}) {
  const uploaderRef = useRef(null);
  const mediaItemsRef = useRef([]);
  if (!uploaderRef.current) {
    uploaderRef.current = createMediaUploader({});
  }
  const uploader = uploaderRef.current;
  const isTask = type === 'task';
  const entityLabel = isTask ? 'task' : 'offering';
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [mediaItems, setMediaItems] = useState([]);
  const [coverIndex, setCoverIndex] = useState(0);
  const [error, setError] = useState('');
  const [uploadPct, setUploadPct] = useState(0);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!open || !item) return;
    const nextMedia = buildPersistedMediaItems(item);
    setTitle(String(item.title || item.name || item.meta?.title || ''));
    setDescription(String(item.description || item.meta?.description || ''));
    setMediaItems((prev) => {
      prev.forEach(revokeBlobPreview);
      return nextMedia;
    });
    setCoverIndex(clampCoverIndex(item.coverIndex, nextMedia.length));
    setError('');
    setUploadPct(0);
    setIsSaving(false);
  }, [open, item]);

  useEffect(() => {
    mediaItemsRef.current = mediaItems;
  }, [mediaItems]);

  useEffect(
    () => () => {
      mediaItemsRef.current.forEach(revokeBlobPreview);
    },
    [],
  );

  const titleLength = title.length;
  const descriptionLength = description.length;
  const titleInvalid = !title.trim() || titleLength > TITLE_MAX_LENGTH;
  const descriptionInvalid =
    !description.trim() || descriptionLength > DESCRIPTION_MAX_LENGTH;
  const canSave = !titleInvalid && !descriptionInvalid && !isSaving;

  const addFiles = useCallback(
    (files) => {
      const list = Array.from(files || []);
      if (!list.length) return;
      const next = [];
      const errors = [];
      list.forEach((file) => {
        try {
          uploader.validate(file);
          next.push({
            file,
            preview: URL.createObjectURL(file),
            isVideo: isVideoFile(file.name),
            persisted: false,
          });
        } catch (err) {
          errors.push(err.message || `${file.name} is not valid.`);
        }
      });
      if (errors.length) setError(errors.join(' | '));
      if (next.length) {
        setMediaItems((prev) => [...prev, ...next]);
      }
    },
    [uploader],
  );

  const removeMedia = useCallback((idx) => {
    setMediaItems((prev) => {
      const next = [...prev];
      const removed = next.splice(idx, 1);
      removed.forEach(revokeBlobPreview);
      return next;
    });
    setCoverIndex((prev) => {
      if (idx === prev) return 0;
      if (idx < prev) return Math.max(0, prev - 1);
      return prev;
    });
  }, []);

  const invalidateMedia = useCallback((idx) => {
    setMediaItems((prev) => {
      if (!prev[idx]) return prev;
      const next = [...prev];
      const old = next[idx];
      revokeBlobPreview(old);
      next[idx] = {
        preview: '',
        url: '',
        isVideo: false,
        placeholder: true,
        label: 'Preview unavailable.',
        needsReattach: true,
      };
      return next;
    });
  }, []);

  const uploadMediaItems = useCallback(async () => {
    if (!mediaItems.length) return [];
    const uploaded = [];
    let completed = 0;
    for (const media of mediaItems) {
      if (media.needsReattach || media.placeholder) {
        throw new Error('MEDIA_REATTACH_REQUIRED');
      }
      if (media.persisted && media.url) {
        uploaded.push({
          url: media.url,
          type: media.isVideo ? 'video' : 'image',
        });
      } else if (media.file) {
        const completedBeforeUpload = completed;
        const { transformedUrl } = await uploader.upload(media.file, {
          onProgress: (pct) => {
            const slice =
              (completedBeforeUpload + pct / 100) / mediaItems.length;
            setUploadPct(Math.round(slice * 100));
          },
          publicIdPrefix: 'Images',
        });
        uploaded.push({
          url: transformedUrl,
          type: media.isVideo ? 'video' : 'image',
        });
      }
      completed += 1;
    }
    setUploadPct(uploaded.length ? 100 : 0);
    return uploaded;
  }, [mediaItems, uploader]);

  const handleSubmit = useCallback(
    async (event) => {
      event.preventDefault();
      if (!canSave) return;
      setIsSaving(true);
      setError('');
      try {
        const media = await uploadMediaItems();
        const payload = {
          title: title.trim(),
          description,
          media,
          coverIndex: clampCoverIndex(coverIndex, media.length),
        };
        await onSave?.(payload);
        onClose?.();
      } catch (err) {
        const message =
          err?.response?.data?.message ||
          (err?.message === 'MEDIA_REATTACH_REQUIRED'
            ? 'Remove or re-add unavailable media before saving.'
            : err?.message) ||
          `Unable to update this ${entityLabel} right now.`;
        setError(message);
      } finally {
        setIsSaving(false);
        setUploadPct(0);
      }
    },
    [
      canSave,
      coverIndex,
      description,
      entityLabel,
      onClose,
      onSave,
      title,
      uploadMediaItems,
    ],
  );

  const titleCountClass =
    titleLength > TITLE_MAX_LENGTH ? 'localops-character-count is-over-limit' : 'localops-character-count';
  const descCountClass =
    descriptionLength > DESCRIPTION_MAX_LENGTH
      ? 'localops-character-count is-over-limit'
      : 'localops-character-count';

  return (
    <LocalOpsSheet
      open={open}
      onClose={isSaving ? undefined : onClose}
      title={`Edit ${entityLabel}`}
    >
      <form className="localops-edit-form" onSubmit={handleSubmit}>
        <div className="localops-info-card">
          Edit wording and media. Price, location, trust, status, and active
          commitments stay unchanged.
        </div>
        <div className="localops-section">
          <div className="localops-field">
            <label>Title</label>
            <input
              type="text"
              value={title}
              maxLength={TITLE_MAX_LENGTH + 20}
              onChange={(event) => setTitle(event.target.value)}
            />
            <div className={titleCountClass}>
              {titleLength}/{TITLE_MAX_LENGTH}
            </div>
          </div>
        </div>
        <div className="localops-section">
          <div className="localops-field">
            <label>Description</label>
            <textarea
              value={description}
              rows={8}
              maxLength={DESCRIPTION_MAX_LENGTH + 100}
              onChange={(event) => setDescription(event.target.value)}
            />
            <div className={descCountClass}>
              {descriptionLength}/{DESCRIPTION_MAX_LENGTH}
            </div>
          </div>
        </div>
        <LocalOpsMediaPicker
          mediaItems={mediaItems}
          coverIndex={coverIndex}
          onCoverChange={setCoverIndex}
          onRemove={removeMedia}
          onInvalidate={invalidateMedia}
          onAddFiles={addFiles}
          error={error}
          uploadPct={uploadPct}
        />
        <div className="localops-actions">
          <button
            type="button"
            className="localops-btn localops-btn--ghost"
            onClick={onClose}
            disabled={isSaving}
          >
            Cancel
          </button>
          <button type="submit" className="localops-btn" disabled={!canSave}>
            {isSaving ? 'Saving...' : 'Save changes'}
          </button>
          {error ? <div className="localops-error">{error}</div> : null}
        </div>
      </form>
    </LocalOpsSheet>
  );
}
